/* eslint-disable max-lines */
import {
  BadgeCheck,
  BarChart3,
  BookOpen,
  CalendarDays,
  CircleDollarSign,
  Mail,
  Settings,
  Users,
} from 'lucide-react'
import { canManage, canView, type StaffCapabilities, type StaffModule } from './staffCapabilities'
import type { NavItem, NavSubItem } from '@/components/templates/app-shell'

interface StaffNavSubItem extends NavSubItem {
  manageOnly?: boolean
  /** This setting is readable by every authenticated Staff Role (feature 29). */
  allStaff?: boolean
}

interface StaffNavItem extends Omit<NavItem, 'children'> {
  children?: StaffNavSubItem[]
}
export type StaffArea =
  | 'event-registrations'
  | 'members'
  | 'organization-memberships'
  | 'contacts'
  | 'chapters'
  | 'contact-duplicates'
  | 'contact-import'
  | 'custom-fields'
  | 'membership-levels'
  | 'renewal-pipeline'
  | 'engagement'
  | 'renewal-settings'
  | 'payment-settings'
  | 'finance-settings'
  | 'tax-settings'
  | 'discount-codes'
  | 'accounting-settings'
  | 'connections'
  | 'unmatched-items'
  | 'email-identity-settings'
  | 'system-email-settings'
  | 'system-email-templates'
  | 'engagement-weights'
  | 'admin-directory-settings'
  | 'seats'
  | 'finance-overview'
  | 'financial-history'
  | 'finance-invoices'
  | 'finance-autopay-recovery'
  | 'finance-tax-report'
  | 'finance-income-report'
  | 'finance-collections-report'
  | 'finance-membership-report'
  | 'finance-aging-report'
  | 'finance-recognition-schedule'
  | 'finance-payouts'
  | 'communication-email-log'
  | 'communication-blasts'
  | 'communication-audiences'
  | 'events'
  | 'courses'
  | 'finance-unreachable-members'
  | 'finance-donations'
  | 'finance-campaigns'
  | 'organization-dashboard-setting'
  | 'credit-unit-settings'

type StaffNavTarget =
  | { kind: 'event-registrations' }
  | { kind: 'list' }
  | { kind: 'chapters' }
  | { kind: 'members' }
  | { kind: 'organization-memberships' }
  | { kind: 'events-list' }
  | { kind: 'courses-list' }
  | { kind: 'duplicates' }
  | { kind: 'import' }
  | { kind: 'custom-fields' }
  | { kind: 'membership-levels' }
  | { kind: 'renewal-pipeline' }
  | { kind: 'engagement' }
  | { kind: 'renewal-settings' }
  | { kind: 'payment-settings' }
  | { kind: 'finance-settings' }
  | { kind: 'tax-settings' }
  | { kind: 'discount-codes' }
  | { kind: 'accounting-settings' }
  | { kind: 'connections' }
  | { kind: 'unmatched-items' }
  | { kind: 'email-identity-settings' }
  | { kind: 'system-email-settings' }
  | { kind: 'system-email-templates' }
  | { kind: 'engagement-weights' }
  | { kind: 'admin-directory-settings' }
  | { kind: 'admin-directory-preview' }
  | { kind: 'seats' }
  | { kind: 'finance-overview' }
  | { kind: 'financial-history' }
  | { kind: 'finance-invoices' }
  | { kind: 'finance-autopay-recovery' }
  | { kind: 'finance-tax-report' }
  | { kind: 'finance-income-report' }
  | { kind: 'finance-collections-report' }
  | { kind: 'finance-membership-report' }
  | { kind: 'finance-aging-report' }
  | { kind: 'finance-recognition-schedule' }
  | { kind: 'finance-payouts' }
  | { kind: 'communication-email-log' }
  | { kind: 'communication-blasts' }
  | { kind: 'communication-audiences' }
  | { kind: 'finance-unreachable-members' }
  | { kind: 'finance-donations' }
  | { kind: 'finance-campaigns' }
  | { kind: 'organization-dashboard-setting' }
  | { kind: 'credit-unit-settings' }

export function staffNav(
  activeArea: StaffArea,
  navigateTo: (view: StaffNavTarget, path: string) => void,
  capabilities?: StaffCapabilities,
) {
  const nav: StaffNavItem[] = [
    {
      label: 'Members',
      icon: BadgeCheck,
      active:
        activeArea === 'members' ||
        activeArea === 'organization-memberships' ||
        activeArea === 'membership-levels' ||
        activeArea === 'renewal-pipeline' ||
        activeArea === 'engagement',
      children: [
        {
          label: 'All members',
          active: activeArea === 'members',
          href: '/members',
          onSelect: () => navigateTo({ kind: 'members' }, '/members'),
        },
        {
          label: 'Organization memberships',
          active: activeArea === 'organization-memberships',
          href: '/members/organizations',
          onSelect: () =>
            navigateTo({ kind: 'organization-memberships' }, '/members/organizations'),
        },
        {
          label: 'Levels',
          active: activeArea === 'membership-levels',
          href: '/members/levels',
          onSelect: () => navigateTo({ kind: 'membership-levels' }, '/members/levels'),
        },
        {
          label: 'Renewal pipeline',
          active: activeArea === 'renewal-pipeline',
          href: '/members/renewal-pipeline',
          onSelect: () => navigateTo({ kind: 'renewal-pipeline' }, '/members/renewal-pipeline'),
        },
        {
          label: 'Engagement',
          active: activeArea === 'engagement',
          href: '/members/engagement',
          onSelect: () => navigateTo({ kind: 'engagement' }, '/members/engagement'),
        },
      ],
    },
    {
      label: 'Contacts',
      icon: Users,
      active: activeArea === 'contacts' || activeArea === 'contact-import',
      children: [
        {
          label: 'All contacts',
          active: activeArea === 'contacts',
          href: '/contacts',
          onSelect: () => navigateTo({ kind: 'list' }, '/contacts'),
        },
        {
          label: 'Chapters',
          active: activeArea === 'chapters',
          href: '/contacts/chapters',
          onSelect: () => navigateTo({ kind: 'chapters' }, '/contacts/chapters'),
        },
        {
          label: 'Duplicates',
          active: activeArea === 'contact-duplicates',
          href: '/contacts/duplicates',
          onSelect: () => navigateTo({ kind: 'duplicates' }, '/contacts/duplicates'),
          manageOnly: true,
        },
        {
          label: 'Import',
          active: activeArea === 'contact-import',
          href: '/contacts/import',
          onSelect: () => navigateTo({ kind: 'import' }, '/contacts/import'),
          manageOnly: true,
        },
      ],
    },
    {
      label: 'Events',
      icon: CalendarDays,
      href: '/events',
      active: activeArea === 'events',
      onSelect: () => navigateTo({ kind: 'events-list' }, '/events'),
    },
    {
      label: 'Courses',
      icon: BookOpen,
      href: '/courses',
      active: activeArea === 'courses',
      onSelect: () => navigateTo({ kind: 'courses-list' }, '/courses'),
    },
    communicationNavigation(activeArea, navigateTo),
    financeNavigation(activeArea, navigateTo),
    reportsNavigation(activeArea, navigateTo),
    settingsNavigation(activeArea, navigateTo, capabilities),
  ]

  if (!capabilities) return nav
  return nav
    .map((item) => {
      const module = moduleForNavLabel(item.label)
      if (item.children) {
        const children = item.children.filter((child) => {
          const childModule = child.module ?? module
          return (
            (child.allStaff || canView(capabilities, childModule)) &&
            (!child.manageOnly || canManage(capabilities, childModule))
          )
        })
        return children.length > 0 ? { ...item, children } : null
      }
      return canView(capabilities, module) ? item : null
    })
    .filter((item): item is (typeof nav)[number] => item !== null)
}

const NAV_MODULES: Record<string, StaffModule> = {
  Members: 'membership',
  Contacts: 'contacts',
  Events: 'events',
  Courses: 'courses',
  Communication: 'communications',
  Finance: 'finance',
  Settings: 'association_settings',
}

function reportsNavigation(
  activeArea: StaffArea,
  navigateTo: (view: StaffNavTarget, path: string) => void,
): StaffNavItem {
  return {
    label: 'Reports',
    icon: BarChart3,
    active: activeArea === 'event-registrations',
    children: [
      {
        label: 'Event registrations',
        module: 'events',
        active: activeArea === 'event-registrations',
        href: '/reports/event-registrations',
        onSelect: () => navigateTo({ kind: 'event-registrations' }, '/reports/event-registrations'),
      },
    ],
  }
}

function moduleForNavLabel(label: string): StaffModule {
  return NAV_MODULES[label] ?? 'association_settings'
}
function communicationNavigation(
  activeArea: StaffArea,
  navigateTo: (view: StaffNavTarget, path: string) => void,
) {
  return {
    label: 'Communication',
    icon: Mail,
    active:
      activeArea === 'communication-email-log' ||
      activeArea === 'communication-blasts' ||
      activeArea === 'communication-audiences',
    children: [
      {
        label: 'Blasts',
        active: activeArea === 'communication-blasts',
        href: '/communication/blasts',
        onSelect: () => navigateTo({ kind: 'communication-blasts' }, '/communication/blasts'),
      },
      {
        label: 'Segments & Groups',
        active: activeArea === 'communication-audiences',
        href: '/communication/audiences',
        onSelect: () => navigateTo({ kind: 'communication-audiences' }, '/communication/audiences'),
      },
      {
        label: 'Email log',
        active: activeArea === 'communication-email-log',
        href: '/communication/email-log',
        onSelect: () => navigateTo({ kind: 'communication-email-log' }, '/communication/email-log'),
      },
    ],
  }
}
function settingsNavigation(
  activeArea: StaffArea,
  navigateTo: (view: StaffNavTarget, path: string) => void,
  capabilities?: StaffCapabilities,
): StaffNavItem {
  return {
    label: 'Settings',
    icon: Settings,
    active:
      activeArea === 'custom-fields' ||
      activeArea === 'renewal-settings' ||
      activeArea === 'payment-settings' ||
      activeArea === 'finance-settings' ||
      activeArea === 'tax-settings' ||
      activeArea === 'discount-codes' ||
      activeArea === 'accounting-settings' ||
      ['connections', 'unmatched-items'].includes(activeArea) ||
      activeArea === 'email-identity-settings' ||
      activeArea === 'system-email-settings' ||
      activeArea === 'system-email-templates' ||
      activeArea === 'engagement-weights' ||
      activeArea === 'admin-directory-settings' ||
      ['credit-unit-settings', 'organization-dashboard-setting'].includes(activeArea),
    children: [
      connectionsNavigation(activeArea, navigateTo, capabilities),
      organizationDashboardNavigation(activeArea, navigateTo),
      {
        label: 'Learning',
        module: 'courses',
        active: activeArea === 'credit-unit-settings',
        href: '/settings/learning',
        onSelect: () => navigateTo({ kind: 'credit-unit-settings' }, '/settings/learning'),
      },
      {
        label: 'Finance',
        active: activeArea === 'finance-settings',
        href: '/settings/finance',
        onSelect: () => navigateTo({ kind: 'finance-settings' }, '/settings/finance'),
      },
      {
        label: 'Renewals',
        active: activeArea === 'renewal-settings',
        href: '/settings/renewals',
        onSelect: () => navigateTo({ kind: 'renewal-settings' }, '/settings/renewals'),
      },
      {
        label: 'Discount Codes',
        module: 'finance',
        active: activeArea === 'discount-codes',
        href: '/settings/discount-codes',
        onSelect: () => navigateTo({ kind: 'discount-codes' }, '/settings/discount-codes'),
      },
      {
        label: 'Taxes',
        active: activeArea === 'tax-settings',
        href: '/settings/taxes',
        onSelect: () => navigateTo({ kind: 'tax-settings' }, '/settings/taxes'),
      },
      {
        label: 'Payments',
        active: activeArea === 'payment-settings',
        href: '/settings/payments',
        onSelect: () => navigateTo({ kind: 'payment-settings' }, '/settings/payments'),
      },
      {
        label: 'Accounting',
        module: 'finance',
        active: activeArea === 'accounting-settings',
        href: '/settings/accounting',
        onSelect: () => navigateTo({ kind: 'accounting-settings' }, '/settings/accounting'),
      },
      {
        label: 'Email',
        active: activeArea === 'email-identity-settings',
        href: '/settings/email',
        onSelect: () => navigateTo({ kind: 'email-identity-settings' }, '/settings/email'),
      },
      {
        label: 'System Emails',
        active: activeArea === 'system-email-settings',
        href: '/settings/system-emails',
        onSelect: () => navigateTo({ kind: 'system-email-settings' }, '/settings/system-emails'),
      },
      {
        label: 'Email Templates',
        active: activeArea === 'system-email-templates',
        href: '/settings/email-templates',
        onSelect: () => navigateTo({ kind: 'system-email-templates' }, '/settings/email-templates'),
      },
      {
        label: 'Custom fields',
        active: activeArea === 'custom-fields',
        href: '/settings/custom-fields',
        onSelect: () => navigateTo({ kind: 'custom-fields' }, '/settings/custom-fields'),
      },
      {
        label: 'Engagement weights',
        active: activeArea === 'engagement-weights',
        href: '/settings/engagement-weights',
        onSelect: () => navigateTo({ kind: 'engagement-weights' }, '/settings/engagement-weights'),
      },
      {
        label: 'Member directory',
        active: activeArea === 'admin-directory-settings',
        href: '/admin/directory/settings',
        onSelect: () =>
          navigateTo({ kind: 'admin-directory-settings' }, '/admin/directory/settings'),
      },
      {
        label: 'Seats',
        active: activeArea === 'seats',
        href: '/settings/seats',
        onSelect: () => navigateTo({ kind: 'seats' }, '/settings/seats'),
      },
    ],
  }
}

function connectionsNavigation(
  activeArea: StaffArea,
  navigateTo: (view: StaffNavTarget, path: string) => void,
  capabilities?: StaffCapabilities,
): StaffNavSubItem {
  const viewList = !capabilities || canView(capabilities, 'connections')
  if (!viewList)
    return {
      label: 'Connections',
      module: 'connection_unmatched',
      active: activeArea === 'unmatched-items',
      href: '/settings/connections/unmatched',
      onSelect: () => navigateTo({ kind: 'unmatched-items' }, '/settings/connections/unmatched'),
    }
  return {
    label: 'Connections',
    module: 'connections',
    active: activeArea === 'connections' || activeArea === 'unmatched-items',
    href: '/settings/connections',
    onSelect: () => navigateTo({ kind: 'connections' }, '/settings/connections'),
  }
}

function financeNavigation(
  activeArea: StaffArea,
  navigateTo: (view: StaffNavTarget, path: string) => void,
) {
  return {
    label: 'Finance',
    icon: CircleDollarSign,
    active:
      activeArea === 'finance-overview' ||
      activeArea === 'financial-history' ||
      activeArea === 'finance-invoices' ||
      activeArea === 'finance-autopay-recovery' ||
      activeArea === 'finance-tax-report' ||
      activeArea === 'finance-income-report' ||
      activeArea === 'finance-collections-report' ||
      activeArea === 'finance-membership-report' ||
      activeArea === 'finance-aging-report' ||
      activeArea === 'finance-recognition-schedule' ||
      activeArea === 'finance-payouts' ||
      activeArea === 'finance-unreachable-members' ||
      activeArea === 'finance-donations' ||
      activeArea === 'finance-campaigns',

    children: [
      {
        label: 'Overview',
        active: activeArea === 'finance-overview',
        href: '/finance',
        onSelect: () => navigateTo({ kind: 'finance-overview' }, '/finance'),
      },
      {
        label: 'Financial history',
        active: activeArea === 'financial-history',
        href: '/finance/financial-history',
        onSelect: () => navigateTo({ kind: 'financial-history' }, '/finance/financial-history'),
      },
      {
        label: 'Invoices',
        active: activeArea === 'finance-invoices',
        href: '/finance/invoices',
        onSelect: () => navigateTo({ kind: 'finance-invoices' }, '/finance/invoices'),
      },
      {
        label: 'Donations',
        active: activeArea === 'finance-donations',
        href: '/finance/donations',
        onSelect: () => navigateTo({ kind: 'finance-donations' }, '/finance/donations'),
      },
      {
        label: 'Campaigns',
        active: activeArea === 'finance-campaigns',
        href: '/finance/donations/campaigns',
        onSelect: () => navigateTo({ kind: 'finance-campaigns' }, '/finance/donations/campaigns'),
      },
      {
        label: 'Failed Autopay',
        active: activeArea === 'finance-autopay-recovery',
        href: '/finance/autopay-recovery',
        onSelect: () =>
          navigateTo({ kind: 'finance-autopay-recovery' }, '/finance/autopay-recovery'),
      },
      {
        label: 'Income report',
        active: activeArea === 'finance-income-report',
        href: '/finance/income-report',
        onSelect: () => navigateTo({ kind: 'finance-income-report' }, '/finance/income-report'),
      },
      {
        label: 'Collections report',
        active: activeArea === 'finance-collections-report',
        href: '/finance/collections-report',
        onSelect: () =>
          navigateTo({ kind: 'finance-collections-report' }, '/finance/collections-report'),
      },
      {
        label: 'Membership report',
        active: activeArea === 'finance-membership-report',
        href: '/finance/membership-report',
        onSelect: () =>
          navigateTo({ kind: 'finance-membership-report' }, '/finance/membership-report'),
      },
      {
        label: 'A/R aging',
        active: activeArea === 'finance-aging-report',
        href: '/finance/aging',
        onSelect: () => navigateTo({ kind: 'finance-aging-report' }, '/finance/aging'),
      },
      {
        label: 'Recognition schedule',
        active: activeArea === 'finance-recognition-schedule',
        href: '/finance/recognition-schedule',
        onSelect: () =>
          navigateTo({ kind: 'finance-recognition-schedule' }, '/finance/recognition-schedule'),
      },
      {
        label: 'Payouts',
        active: activeArea === 'finance-payouts',
        href: '/finance/payouts',
        onSelect: () => navigateTo({ kind: 'finance-payouts' }, '/finance/payouts'),
      },
      {
        label: 'Tax report',
        active: activeArea === 'finance-tax-report',
        href: '/finance/tax-report',
        onSelect: () => navigateTo({ kind: 'finance-tax-report' }, '/finance/tax-report'),
      },
      {
        label: "Can't reach",
        active: activeArea === 'finance-unreachable-members',
        href: '/finance/cant-reach',
        onSelect: () => navigateTo({ kind: 'finance-unreachable-members' }, '/finance/cant-reach'),
      },
    ],
  }
}

function organizationDashboardNavigation(
  activeArea: StaffArea,
  navigateTo: (view: StaffNavTarget, path: string) => void,
): StaffNavSubItem {
  return {
    label: 'Organization dashboard',
    allStaff: true,
    active: activeArea === 'organization-dashboard-setting',
    href: '/settings/organization-dashboard',
    onSelect: () =>
      navigateTo({ kind: 'organization-dashboard-setting' }, '/settings/organization-dashboard'),
  }
}
